if (!customElements.get('product-card-swiper')) {
  customElements.define('product-card-swiper', class extends HTMLElement {
    connectedCallback() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this._tryInit(0), { once: true });
      } else {
        this._tryInit(0);
      }
    }

    _tryInit(attempts) {
      if (this._initialized) return;
      if (this.querySelector('.product-card__slider')) {
        this._initialized = true;
        this.init();
        return;
      }
      if (attempts < 120) {
        requestAnimationFrame(() => this._tryInit(attempts + 1));
      }
    }

    init() {
      this.slider = this.querySelector('.product-card__slider');
      this.allSlides = Array.from(this.querySelectorAll('.product-card__slide'));
      this.allDots = Array.from(this.querySelectorAll('.product-card__dot'));
      this.prevBtn = this.querySelector('[data-direction="prev"]');
      this.nextBtn = this.querySelector('[data-direction="next"]');
      if (!this.slider) return;

      this._lockAspectToFirstSlide();

      this.slider.addEventListener('scroll', () => {
        if (this._raf) cancelAnimationFrame(this._raf);
        this._raf = requestAnimationFrame(() => this.update());
      }, { passive: true });

      this.addEventListener('touchstart', () => {
        this.classList.add('is-touched');
      }, { passive: true, once: true });

      const wireArrow = (btn, dir) => {
        if (!btn) return;
        btn.addEventListener('mousedown', (e) => { e.stopPropagation(); });
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          const w = this.slider.clientWidth;
          this.slider.scrollBy({ left: w * dir, behavior: 'smooth' });
        });
      };
      wireArrow(this.prevBtn, -1);
      wireArrow(this.nextBtn, 1);

      const card = this.closest('product-card');
      if (card) {
        card.addEventListener('change', (e) => this.handleVariantChange(e));
        card.addEventListener('click', (e) => this.handleSwatchClick(e));
      }

      const defaultId = this.dataset.defaultVariantId;
      if (defaultId) {
        this.filterByVariant(defaultId, false);
      } else {
        this.update();
      }

      requestAnimationFrame(() => this.update());
      requestAnimationFrame(() => this.classList.add('is-ready'));
    }

    _lockAspectToFirstSlide() {
      const firstSlide = this.allSlides[0];
      if (!firstSlide) return;
      const firstImg = firstSlide.querySelector('img');
      if (!firstImg) return;

      const apply = () => {
        const w = firstImg.naturalWidth;
        const h = firstImg.naturalHeight;
        if (!w || !h) return;
        this.style.setProperty('--first-slide-aspect', w + ' / ' + h);
      };

      if (firstImg.complete && firstImg.naturalWidth) {
        apply();
      } else {
        firstImg.addEventListener('load', apply, { once: true });
      }
    }

    handleVariantChange(e) {
      const target = e.target;
      if (target.matches && (target.matches('input[name^="swatch-"]') || target.dataset.variantId)) {
        const variantId = target.dataset.variantId || target.value;
        if (variantId) this.filterByVariant(variantId);
      }
    }

    handleSwatchClick(e) {
      if (e.target.closest('.product-card__arrow')) return;
      const swatch = e.target.closest && e.target.closest('[data-variant-id]');
      if (swatch && swatch.dataset.variantId && !swatch.classList.contains('product-card__slide') && !swatch.classList.contains('product-card__dot')) {
        this.filterByVariant(swatch.dataset.variantId);
      }
    }

    filterByVariant(variantId, smooth = true) {
      const id = String(variantId);
      let firstVisible = null;

      this.allSlides.forEach((slide) => {
        const slideVariantId = slide.dataset.variantId || '';
        const matches = !slideVariantId || slideVariantId === id;
        slide.classList.toggle('is-hidden-variant', !matches);
        if (matches && !firstVisible) firstVisible = slide;
      });

      this.allDots.forEach((dot) => {
        const dotVariantId = dot.dataset.variantId || '';
        const matches = !dotVariantId || dotVariantId === id;
        dot.classList.toggle('is-hidden-variant', !matches);
      });

      if (firstVisible) {
        this.slider.scrollTo({ left: firstVisible.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
      }

      this.update();
    }

    update() {
      const visibleSlides = this.allSlides.filter(s => !s.classList.contains('is-hidden-variant'));
      const visibleDots = this.allDots.filter(d => !d.classList.contains('is-hidden-variant'));

      if (!visibleSlides.length) return;

      const slideWidth = this.slider.clientWidth;
      let clampedIdx = 0;
      if (slideWidth > 0) {
        const idx = Math.round(this.slider.scrollLeft / slideWidth);
        clampedIdx = Math.min(Math.max(0, idx), visibleSlides.length - 1);
      }

      const MAX_DOTS = 8;
      let windowStart = 0;
      if (visibleDots.length > MAX_DOTS) {
        windowStart = Math.max(
          0,
          Math.min(clampedIdx - Math.floor(MAX_DOTS / 2), visibleDots.length - MAX_DOTS)
        );
      }

      visibleDots.forEach((d, i) => {
        const inWindow = i >= windowStart && i < windowStart + MAX_DOTS;
        d.classList.toggle('is-overflow', !inWindow);
        d.classList.toggle('is-active', i === clampedIdx);
      });

      if (this.prevBtn) {
        this.prevBtn.classList.toggle('is-hidden', clampedIdx === 0);
      }
      if (this.nextBtn) {
        this.nextBtn.classList.toggle('is-hidden', clampedIdx >= visibleSlides.length - 1);
      }
    }
  });
}